import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowRight, ArrowDown, Smartphone, Tablet, Monitor } from 'lucide-react';

/**
 * Direction options
 */
const DIRECTIONS = [
  { value: 'horizontal', label: 'Horizontal (Side by Side)', icon: ArrowRight },
  { value: 'vertical', label: 'Vertical (Stacked)', icon: ArrowDown },
];

/**
 * Breakpoint definitions
 */
const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', short: 'M', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', short: 'T', icon: Tablet },
  { id: 'desktop', label: 'Desktop', short: 'D', icon: Monitor },
];

/**
 * ResponsiveDirectionField - Direction selector with breakpoint support
 *
 * Supports responsive direction values for different screen sizes.
 * Non-base breakpoints can inherit from base or have overrides.
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Object} [props.field.default] - Default responsive direction
 * @param {Object} props.value - Current value { base, tablet, desktop }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 * @param {string} [props.activeBreakpoint] - External breakpoint control (syncs with canvas preview)
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes (to resize canvas)
 */
export default function ResponsiveDirectionField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
  compact = false,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [localBreakpoint, setLocalBreakpoint] = useState('base');

  // Use external breakpoint if provided, otherwise use local state
  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);  // Notify parent to resize canvas
    } else {
      setLocalBreakpoint(bp);  // Local only
    }
  };

  // Normalize to responsive format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      // Already in responsive format
      if (val.base !== undefined) {
        return {
          base: val.base || 'horizontal',
          tablet: val.tablet || null,
          desktop: val.desktop || null
        };
      }
      // Unexpected format
      console.warn('ResponsiveDirectionField: Unexpected format, defaulting to horizontal', val);
      return {
        base: 'horizontal',
        tablet: null,
        desktop: null
      };
    }
    if (typeof val === 'string') {
      // Legacy string format - convert to responsive
      return {
        base: val,
        tablet: null,
        desktop: null
      };
    }
    return field.default || {
      base: 'horizontal',
      tablet: null,
      desktop: null
    };
  };

  const responsiveObj = normalizeValue(value);

  // Get current direction for active breakpoint (with fallback to base)
  const currentDirection = responsiveObj[activeBreakpoint] || responsiveObj.base || 'horizontal';

  const updateDirection = (newDirection) => {
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newDirection
    };
    onChange(newResponsiveObj);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null
      };
      onChange(newResponsiveObj);
    }
  };

  // Whether current breakpoint is inheriting from a lower breakpoint
  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];

  // Compact mode: inline breakpoint pills + direction icons
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex items-center gap-2 flex-1">
          {/* Breakpoint pills */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
            {BREAKPOINTS.map((bp) => {
              const Icon = bp.icon;
              const hasOverride = bp.id !== 'base' && responsiveObj[bp.id];
              return (
                <button
                  key={bp.id}
                  type="button"
                  onClick={() => handleBreakpointChange(bp.id)}
                  disabled={disabled}
                  title={bp.label}
                  className={`relative p-1 rounded transition-colors
                    ${activeBreakpoint === bp.id
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                  <Icon className="w-3 h-3" />
                  {hasOverride && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
          {/* Direction toggle icons */}
          <div className={`flex items-center gap-0.5 bg-gray-100 rounded p-0.5 ${isInheriting ? 'opacity-60' : ''}`}>
            {DIRECTIONS.map((dir) => {
              const Icon = dir.icon;
              const isActive = currentDirection === dir.value;
              return (
                <button
                  key={dir.value}
                  type="button"
                  onClick={() => updateDirection(dir.value)}
                  disabled={disabled}
                  title={`${dir.label}${isInheriting ? ' (inherited from base)' : ''}`}
                  className={`p-1.5 rounded transition-colors
                    ${isActive
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => (
    <div className="space-y-3">
      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {BREAKPOINTS.map((bp) => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleBreakpointChange(bp.id)}
            disabled={disabled}
          >
            {bp.label}
            {/* Dot indicator for overrides */}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Direction options */}
      <div className="space-y-2">
        {DIRECTIONS.map((direction) => (
          <label
            key={direction.value}
            className={`flex items-center p-2 border rounded cursor-pointer transition-colors ${
              currentDirection === direction.value
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input
              type="radio"
              name={`${field.key}-direction-${activeBreakpoint}`}
              value={direction.value}
              checked={currentDirection === direction.value}
              onChange={(e) => updateDirection(e.target.value)}
              disabled={disabled}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-xs text-gray-700">{direction.label}</span>
          </label>
        ))}
      </div>

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          disabled={disabled}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from base)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          Currently inheriting from Mobile (base)
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
